import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Divider,
} from '@mui/material';
import { notificationsAPI } from '../../services/api';
import { toAppPath } from '../../utils/notificationPaths';

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');

  const listQuery = useQuery(
    ['notifications-list', filter],
    async () => {
      const params = { limit: 50 };
      if (filter === 'unread') params.unreadOnly = 'true';
      if (filter === 'needs_action') {
        params.unreadOnly = 'true';
        params.category = 'needs_action';
      }
      if (filter === 'information') params.category = 'information';
      const res = await notificationsAPI.list(params);
      return res.data?.data || [];
    }
  );

  const markReadMutation = useMutation(
    (id) => notificationsAPI.markRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications-list']);
        queryClient.invalidateQueries(['notifications-unread-count']);
        queryClient.invalidateQueries(['notifications-important-updates']);
      },
    }
  );

  const markAllMutation = useMutation(
    () => notificationsAPI.markAllRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications-list']);
        queryClient.invalidateQueries(['notifications-unread-count']);
        queryClient.invalidateQueries(['notifications-important-updates']);
      },
    }
  );

  const handleClick = async (item) => {
    if (!item.readAt) {
      try {
        await markReadMutation.mutateAsync(item.id);
      } catch (_) {}
    }
    if (item.actionUrl) {
      const path = toAppPath(item.actionUrl);
      if (path) navigate(path);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2, gap: 2 }}>
        <Typography variant="h5">Notifications</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" value={filter} exclusive onChange={(_, v) => v && setFilter(v)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="unread">Unread</ToggleButton>
            <ToggleButton value="needs_action">Needs action</ToggleButton>
            <ToggleButton value="information">Information</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" onClick={() => markAllMutation.mutate()} disabled={markAllMutation.isLoading}>
            Mark all read
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        {listQuery.isLoading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (listQuery.data || []).length === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary">
            No notifications to show.
          </Typography>
        ) : (
          <List disablePadding>
            {(listQuery.data || []).map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItemButton onClick={() => handleClick(item)} sx={{ bgcolor: item.isUnread ? 'action.hover' : undefined }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" fontWeight={item.isUnread ? 700 : 500}>
                          {item.title}
                        </Typography>
                        {item.category === 'needs_action' && <Chip size="small" color="warning" label="Action" />}
                        {item.isUnread && <Chip size="small" variant="outlined" label="Unread" />}
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-line', display: 'block', mt: 0.5 }}>
                          {item.body}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {formatWhen(item.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
